import { useEffect, useRef, useState } from "react";

import { TOOLTIP_SURFACE } from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";

/** How far above or below the clipped box the preview sits. */
const GAP = 6;
/** Kept off the window edges by this much when a long value is centred out of view. */
const MARGIN = 8;

type Shown = { text: string; left: number; y: number; side: "top" | "bottom" };

/**
 * Whether this element is showing less than it holds.
 *
 * `scrollWidth > clientWidth` is the only honest test: it answers at the
 * element's real width, in the reader's own font, after the browser has laid
 * the text out. Nothing else — not a character count, not a width guess —
 * knows whether *this* value in *this* column got an ellipsis.
 */
function clipped(element: HTMLElement): boolean {
  return element.scrollWidth > element.clientWidth;
}

/**
 * The whole value, on hover, wherever the screen had to cut one short.
 *
 * One listener for the entire application rather than a wrapper at each of the
 * ~69 places that clip text. Two reasons, and the second is the one that
 * matters: a wrapper has to be remembered, and the next table someone writes
 * will not remember it, so coverage would decay from the day it was complete.
 * The delegated listener covers what is there now and what is written later,
 * because it keys off the thing that *causes* the problem.
 *
 * That key is `.truncate` — Tailwind's `overflow:hidden; text-overflow:
 * ellipsis; white-space:nowrap`. Every clipping element in this application
 * carries it and nothing else does, so `closest` finds the clipped box from
 * whatever the pointer is actually over and ignores everything else for the
 * cost of a selector match. Deliberately not "any element whose content
 * overflows": that is also true of every horizontal scroller, and a preview
 * over a scrollable table is nonsense.
 *
 * In a popover host, for the reason the toaster is: every overlay here is a
 * native `<dialog>` opened with `showModal()`, so it sits in the browser's top
 * layer and an ordinary fixed box — whatever its z-index — is painted behind
 * it. A value clipped inside a dialog is exactly the kind worth previewing, so
 * the preview is promoted to the top layer each time it is shown, which puts
 * it after whatever dialog is already there.
 *
 * Fixed coordinates rather than a positioned ancestor: a clipped cell is
 * usually inside something that scrolls, and an absolutely-placed box is
 * clipped by the same overflow that cut the text.
 */
export function ClipPreview() {
  const [shown, setShown] = useState<Shown | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const show = (event: Event) => {
      const target = event.target;
      const element = target instanceof Element ? target.closest(".truncate") : null;
      if (!(element instanceof HTMLElement) || !clipped(element)) return setShown(null);
      // `textContent`, not a prop: the listener never sees what React was
      // given, and a cell that draws its value through two spans still reads
      // as the one string a person was trying to finish.
      const text = element.textContent?.trim();
      if (!text) return setShown(null);

      const rect = element.getBoundingClientRect();
      const above = rect.top > window.innerHeight - rect.bottom;
      setShown({
        text,
        left: Math.round(rect.left + rect.width / 2),
        y: Math.round(above ? rect.top - GAP : rect.bottom + GAP),
        side: above ? "top" : "bottom",
      });
    };
    const hide = () => setShown(null);

    // Captured, so a pointer entering a cell inside a dialog or a scroller is
    // seen the same as one over the page.
    document.addEventListener("pointerover", show, true);
    // A scroll moves the box out from under its preview, and a press means the
    // person is doing something other than reading.
    document.addEventListener("scroll", hide, true);
    document.addEventListener("pointerdown", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      document.removeEventListener("pointerover", show, true);
      document.removeEventListener("scroll", hide, true);
      document.removeEventListener("pointerdown", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  useEffect(() => {
    const element = host.current;
    if (!element || !("showPopover" in element)) return;
    try {
      if (element.matches(":popover-open")) element.hidePopover();
      // Promoted on every show, never once at mount: the top layer is a stack,
      // and the dialog opened after us would otherwise be in front.
      if (shown) element.showPopover();
    } catch {
      // A browser with no top layer paints it the old way, which is fine.
    }
  }, [shown]);

  if (!shown) return null;
  return (
    <div
      ref={host}
      popover="manual"
      className="fixed inset-auto m-0 size-0 overflow-visible border-0 bg-transparent p-0"
    >
      <div
        ref={box}
        role="tooltip"
        style={{
          left: shown.left,
          ...(shown.side === "top"
            ? { bottom: window.innerHeight - shown.y }
            : { top: shown.y }),
          maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
        }}
        // `-translate-x-1/2` centres it on the box it describes; the browser
        // keeps it on screen horizontally only because `max-width` bounds it,
        // so a very long value wraps rather than running off the edge.
        className={cn(TOOLTIP_SURFACE, "fixed -translate-x-1/2")}
      >
        {shown.text}
      </div>
    </div>
  );
}
